import { useContext, useState } from 'react'
import { jt, t } from 'ttag'
import { ReactBootstrapSlider } from 'react-bootstrap-slider'
import { useMutation, useQuery } from 'react-query'
import qs from 'qs'
import { useLocation } from 'react-router-dom'

import { Text } from '@h2/components/typography/text'
import { Page } from '@h2/common/components/page'
import { Globals } from '@h2/globals'
import { api } from '@h2/api'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'
import { Link } from '@h2/common/components/link'

import { RoundCost } from './components/roundCost'
import { budgetRounds, fmtCurrency } from './common/helpers'
import { HorizontalSummary } from './components/horizontalSummary'

const BudgetReverse = props => {
  const { budget } = props
  const qsParser = qs.parse(useLocation().search, { ignoreQueryPrefix: true })
  const requestedOnetSoc = qsParser.onetSoc
  const navigator = useNavigator()
  const [amount, setAmount] = useState(0)
  const { data: reverseWage } = useQuery(
    ['budget-wages', requestedOnetSoc, budget?.id],
    () => {
      const onetQs = requestedOnetSoc ? `?onetSoc=${requestedOnetSoc}` : ''

      return api.fetch(`/budget/${budget?.id ?? -1}/wages${onetQs}`, {
        method: 'GET'
      })
    },
    {
      onSuccess: resp => {
        setAmount(resp?.annualMean?.amount ?? 0)
      }
    }
  )
  const { responses } = budget ?? {}
  const { budgetTitle } = useContext(Globals)
  const rounds = budgetRounds()
  const { mutate: saveSalary, isLoading: isSaving } = useMutation(
    form => {
      const param = requestedOnetSoc ? `?onetSoc=${requestedOnetSoc}` : ``

      return api.fetch(`/budget/${budget?.id ?? -1}/respond/salary${param}`, {
        method: 'POST',
        body: JSON.stringify({ salary: form.monthlyCost })
      })
    },
    {
      onSuccess: newBudget => {
        navigator(`/budget/${newBudget.id}/location`)
      },
      onError: () => {
        //const formErrors = errors.getValidation(err)
        /*
         *if (formErrors) {
         *dispatch({ type: 'validate', payload: formErrors })
         *}
         */
      }
    }
  )
  const coalesce = (wage1, wage2, wage3) => {
    return wage1 ? wage1.amount : wage2 ? wage2.amount : wage3 ? wage3.amount : null
  }
  const coalesceWage = (wage1, wage2, naMsg) => {
    const wage = coalesce(wage1, wage2)

    if (!wage) {
      return naMsg
    }
    //console.log(w)

    return fmtCurrency(wage)
  }
  const addVal = inp => {
    return inp || inp === 0 ? inp.amount : null
  }
  const calcPct = (value, max) => {
    return 100 * (value / max)
  }
  const maxSalary = 200000
  const rangeHighlights = [reverseWage?.annual10, reverseWage?.annual90]
  const ticks = [
    0,
    ...[
      addVal(reverseWage?.annual10),
      addVal(reverseWage?.annual25),
      addVal(reverseWage?.annualMean),
      addVal(reverseWage?.annual75),
      addVal(reverseWage?.annual90)
    ].filter(value => !!value),
    maxSalary
  ]
  const tickPcts = ticks.map(tick => calcPct(tick, maxSalary))
  const topWage = (
    <b key="topWage">
      {coalesceWage(reverseWage?.annual90, reverseWage?.annual75, t`n/a`)}
    </b>
  )
  const bottomWage = (
    <b key="bottomWage">
      {coalesceWage(reverseWage?.annual10, reverseWage?.annual25, t`n/a`)}
    </b>
  )
  const meanWage = (
    <b key="meanWage">{coalesceWage(reverseWage?.annualMean, null, t`n/a`)}</b>
  )
  const occLink = reverseWage?.occupation && (
    <Link weight="bold" size="large" href={reverseWage.occupation.url}>
      {reverseWage.occupation.title}
    </Link>
  )
  const occSpecific = reverseWage?.occupation ? jt`for the occupation ${occLink}` : null
  const occTitle = reverseWage?.occupation?.title
  const loc = reverseWage?.location

  return (
    <Page>
      <Page.Title>{`${budgetTitle} - Salary`}</Page.Title>
      <Page.Layout>
        <Page.Content>
          <div>
            <div className="occupation">
              <Text size="large">{jt`You are about to begin a budget ${occSpecific}.`}</Text>
              <Text size="large">
                {reverseWage?.occupation
                  ? jt`Most ${occTitle} in ${loc} make between ${bottomWage} and ${topWage}.`
                  : jt`The average worker across all occupations in ${loc} make between ${bottomWage} and ${topWage}.`}{' '}
                {t`Depending on your experience, you can generally expect to make somewhere in this range.
                            You can enter your expected salary in the box below, or use the slider to select one of the common values and fill it in.`}
              </Text>

              <div id="imgcontainer">
                <div className="low withicons"></div>
                <div className="med withicons"></div>
                <div className="high withicons"></div>
              </div>

              <div className="slider-holder">
                <ReactBootstrapSlider
                  min={0}
                  step={1000}
                  max={maxSalary}
                  selection="none"
                  rangeHighlight={rangeHighlights}
                  tooltip_position="bottom"
                  formatter={fmtCurrency}
                  change={value => {
                    //console.log(v)
                    setAmount(value.target.value)
                  }}
                  value={amount}
                  ticks={ticks}
                  ticks_labels={ticks.map(tick => fmtCurrency(tick))}
                  ticks_positions={tickPcts}
                />
              </div>

              <Text align="center" size="large" margin="15px 0">
                {jt`The average wage for this occupation is ${meanWage} per year`}
              </Text>
              <br />
            </div>
            <RoundCost
              direction="row"
              currentRound={-2}
              rounds={rounds}
              onUpdateValue={form => saveSalary(form)}
              isSubmitting={isSaving}
              onMoveNext={evt => {
                evt.preventDefault()
                navigator(`/budget/${budget?.id}/question/0`)
              }}
              value={amount}
            />
          </div>
          <br />
          {budget && (
            <HorizontalSummary
              occupation={budget.occupation}
              startingSalary={budget.startSalary}
              location={budget.county}
              currentRoundId={-2}
              budgetId={budget.id}
              reverse={budget.reverse}
              monthlyTotal={0}
              rounds={rounds}
              responses={responses}
            />
          )}
        </Page.Content>
      </Page.Layout>
    </Page>
  )
}

export { BudgetReverse }
